/**
 * Reglas de obligatoriedad de declaración de renta — solo persona natural.
 * Jurídica usa el flag `obligatorio` de la API de solicitud (PAR-117 muta
 * renta/ventas: si no aplica, el doc va al paso de opcionales).
 * Alineado a back PN: ValidacionDocumentoRenta (PAR-096) + validarValorVentas (PAR-117).
 */

/** Meses entre fechas (equivalente práctico a ChronoUnit.MONTHS.between). */
export function monthsBetweenDates(
  from: Date,
  to: Date = new Date()
): number {
  let months =
    (to.getFullYear() - from.getFullYear()) * 12 +
    (to.getMonth() - from.getMonth());
  if (to.getDate() < from.getDate()) {
    months -= 1;
  }
  return months;
}

export interface AplicaDeclaracionRentaInput {
  /** Null si no tiene cámara / no hay fecha (regla antigüedad no aplica). */
  fechaRegistroActividadEconomica?: string | Date | null;
  /** Ventas anuales del empresario (registro). */
  valorVentas?: number | string | null;
  /** PAR-096. null = no evaluar antigüedad. */
  tiempoMinimoMeses?: number | null;
  /** PAR-117. null = no evaluar ventas. */
  valorLimiteVentas?: number | null;
}

/**
 * true si aplica por antigüedad (fecha + PAR-096) o por ventas (valorVentas + PAR-117).
 * Si aplica, el doc es obligatorio sin preguntar (ignora flag opcional del catálogo).
 */
export function aplicaDeclaracionRentaPorRegla(
  input: AplicaDeclaracionRentaInput
): boolean {
  const porAntiguedad = aplicaPorAntiguedad(
    input.fechaRegistroActividadEconomica,
    input.tiempoMinimoMeses
  );
  const porVentas = aplicaPorVentas(
    input.valorVentas,
    input.valorLimiteVentas
  );
  return porAntiguedad || porVentas;
}

function aplicaPorAntiguedad(
  fechaRaw: unknown,
  tiempoMinimoMeses: number | null | undefined
): boolean {
  if (tiempoMinimoMeses == null || !Number.isFinite(tiempoMinimoMeses)) {
    return false;
  }
  if (fechaRaw == null || fechaRaw === '') {
    return false;
  }
  const fecha = new Date(fechaRaw as string | Date);
  if (Number.isNaN(fecha.getTime())) {
    return false;
  }
  return monthsBetweenDates(fecha) >= tiempoMinimoMeses;
}

function aplicaPorVentas(
  valorVentasRaw: unknown,
  valorLimite: number | null | undefined
): boolean {
  if (valorLimite == null || !Number.isFinite(valorLimite)) {
    return false;
  }
  const ventas = Number(valorVentasRaw);
  if (!Number.isFinite(ventas)) {
    return false;
  }
  // Misma comparación que back: parametro <= valorVentas
  return valorLimite <= ventas;
}
